import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Nav, TabContent, TabPane, NavLink, NavItem } from 'reactstrap';
import { checkTabs } from 'supports/helpers/permissions';
import {
  fetchPoRows,
  fetchBranches,
  fetchTujuanPengeluaran,
} from 'redux/modules/purchaseOrder';
import { TAB_NAMES, DETAIL_TAB, TABS, MODES, defaultFilter } from './config';
import PoListTab from './list/PoListTab';
import PoDataTab from './detail/PoDataTab';

/**
 * Purchase Order page: a permission-filtered tab bar over one list tab
 * (config-driven) and the PO Data tab. Page state is just the active tab,
 * the applied list filter, and which PO (and in which mode) PO Data shows.
 */
const PurchaseOrderPage = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const page = new URLSearchParams(location.search).get('page');
  const access = useSelector((state) => state.auth.access);
  const tabAccess = useMemo(() => checkTabs(access, TAB_NAMES), [access]);

  const [activeTab, setActiveTab] = useState(null);
  const [appliedFilter, setAppliedFilter] = useState({});
  const [selection, setSelection] = useState(null); // { id, mode } | null

  const selectTab = useCallback(
    (tab) => {
      setActiveTab(tab);
      if (tab === DETAIL_TAB) return; // URL is written by the sync effect below
      history.replace(`?page=1&tab=${tab}`);
      setAppliedFilter(defaultFilter(TABS[tab]));
      setSelection(null);
    },
    [history],
  );

  const openDetail = useCallback((id, mode) => {
    setActiveTab(DETAIL_TAB);
    setSelection({ id, mode });
  }, []);

  // PO Data keeps its PO and mode in the URL so a refresh reopens the same PO.
  useEffect(() => {
    if (activeTab !== DETAIL_TAB) return;
    history.replace(
      selection
        ? `?tab=${DETAIL_TAB}&id=${selection.id}&mode=${selection.mode}`
        : `?tab=${DETAIL_TAB}`,
    );
  }, [activeTab, selection, history]);

  useEffect(() => {
    dispatch(fetchBranches());
    dispatch(fetchTujuanPengeluaran());
  }, [dispatch]);

  // On mount, restore the tab (and page / open PO) from the URL; otherwise
  // start on the first permitted tab.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (!tabAccess.includes(tab)) {
      if (tabAccess[0]) selectTab(tabAccess[0]);
      return;
    }
    setActiveTab(tab);
    if (tab === DETAIL_TAB) {
      const id = params.get('id');
      const mode = params.get('mode');
      if (id && MODES[mode]) setSelection({ id: Number(id), mode });
      return;
    }
    setAppliedFilter(defaultFilter(TABS[tab]));
    if (!params.get('page')) history.replace(`?page=1&tab=${tab}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabAccess, selectTab]);

  const fetchRows = useCallback(() => {
    const tab = TABS[activeTab];
    if (!tab || !page) return;
    dispatch(
      fetchPoRows(tab.endpoint, { page, ...tab.flags, ...appliedFilter }),
    );
  }, [dispatch, activeTab, page, appliedFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const listTab = TABS[activeTab];
  let activePane = null;
  if (activeTab === DETAIL_TAB) activePane = 'detail';
  else if (listTab) activePane = 'list';

  return (
    <div className="white-box py-2 container-fluid">
      <div className="row py-4">
        <div className="col-12 offset-md-1 col-md-10 py-3">
          <div>
            <div className="line-green mb-2" />
            <div className="general-title text-gray">Purchase Order</div>
          </div>
        </div>
        <div className="col-12 offset-md-1 col-md-10 py-3">
          <Nav tabs>
            {tabAccess.map((tab) => (
              <NavItem key={tab}>
                <NavLink
                  active={activeTab === tab}
                  onClick={() => selectTab(tab)}
                >
                  {tab}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
          <TabContent activeTab={activePane}>
            <TabPane tabId="list">
              {listTab && (
                <PoListTab
                  key={listTab.name}
                  tab={listTab}
                  page={page}
                  appliedFilter={appliedFilter}
                  onSearch={setAppliedFilter}
                  openDetail={openDetail}
                  refetch={fetchRows}
                />
              )}
            </TabPane>
            <TabPane tabId="detail">
              <PoDataTab selection={selection} setSelection={setSelection} />
            </TabPane>
          </TabContent>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderPage;
